import React, { useState } from "react";
import { CheckCircle, Clock, XCircle, CalendarBlank, MapPin, Buildings, Lightning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

/**
 * Candidate-facing application status stepper.
 *
 * Shown on the candidate dashboard for every active application. Steps:
 *   Applied → Screening → Interview → Offer → Hired
 * Rejected shows a distinct closed state instead of a step.
 *
 * States per step:
 *   - done      (past stage): filled ink circle + check + timestamp
 *   - current   (this stage): black outline circle + label bold
 *   - upcoming  (later stage): neutral gray outline + light label
 */
const STEPS = ["Applied", "Screening", "Interview", "Offer", "Hired"];

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch { return ""; }
};
const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return ""; }
};

export default function ApplicationTracker({ application, onBoosted }) {
    const stage = application.stage;
    const isRejected = stage === "Rejected";
    const currentIdx = isRejected ? -1 : STEPS.indexOf(stage);
    const history = application.stage_history || [];
    // Latest timestamp per stage (from stage_history)
    const stageAt = {};
    for (const h of history) {
        if (h.stage) stageAt[h.stage] = h.at;
    }

    // Application boost state
    const boost = application.application_boost || null;
    const boostActive = boost?.active_until
        ? (() => { try { return new Date(boost.active_until) > new Date(); } catch { return false; } })()
        : false;
    const [buying, setBuying] = useState(false);

    const startBoost = async () => {
        setBuying(true);
        try {
            const { data: order } = await api.post(`/me/applications/${application.id}/boost/checkout`);
            // Credit fast-path — no Razorpay round-trip.
            if (order.paid_by_credit) {
                toast.success(`Boosted with a credit · ${order.credits_remaining} left`);
                onBoosted?.();
                return;
            }
            const rzOk = await loadRazorpay();
            if (!rzOk) {
                toast.error("Couldn't load payment gateway. Please try again.");
                return;
            }
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: "Boost this application — 7 days",
                theme: { color: "#1a1a1a" },
                handler: async (rp) => {
                    try {
                        await api.post(`/me/applications/${application.id}/boost/verify`, {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        toast.success("Boosted! You're at the top of the recruiter shortlist.");
                        onBoosted?.();
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || "Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBuying(false) },
            });
            rz.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start checkout");
        } finally {
            setBuying(false);
        }
    };

    // Upcoming interview (first future entry)
    const upcoming = (application.interviews || []).find((iv) => {
        try { return new Date(iv.scheduled_at) > new Date(); } catch { return false; }
    });

    const job = application.job || {};
    const client = job.client || {};

    return (
        <div
            data-testid={`app-tracker-${application.id}`}
            className="border border-[#E5E5E5] bg-white p-5 sm:p-6"
        >
            {/* Job header */}
            <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        {application.code || "APPLICATION"}
                        {application.created_at && <> · applied {fmtDate(application.created_at)}</>}
                    </p>
                    <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-ink leading-tight">
                        {job.title || "Untitled role"}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#525252] flex-wrap">
                        {client.name && (
                            <span className="inline-flex items-center gap-1">
                                <Buildings size={11} weight="bold" /> {client.name}
                            </span>
                        )}
                        {job.location && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin size={11} weight="bold" /> {job.location}
                            </span>
                        )}
                        {job.seniority && <span>{job.seniority}</span>}
                    </div>
                </div>
                {isRejected ? (
                    <span className="text-[10px] font-black uppercase tracking-wider bg-rose-100 text-rose-900 px-2.5 py-1">
                        Not moving forward
                    </span>
                ) : stage === "Hired" ? (
                    <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-900 px-2.5 py-1">
                        Hired &check;
                    </span>
                ) : (
                    <span className="text-[10px] font-black uppercase tracking-wider bg-ink text-white px-2.5 py-1">
                        {stage}
                    </span>
                )}
            </div>

            {/* Stepper */}
            {isRejected ? (
                <div className="border-l-4 border-l-rose-500 bg-rose-50 px-4 py-3">
                    <p className="text-sm font-bold text-rose-900 mb-0.5">Application closed</p>
                    <p className="text-xs text-rose-800">
                        {stageAt.Rejected
                            ? `The team decided not to move forward on ${fmtDate(stageAt.Rejected)}. Not the right role — we'll keep matching you to others.`
                            : "The team decided not to move forward. We'll keep matching you to other roles."}
                    </p>
                </div>
            ) : (
                <div className="relative">
                    <div className="flex items-center justify-between relative">
                        {/* Connector line — behind circles */}
                        <div className="absolute top-4 left-4 right-4 h-[2px] bg-[#E5E5E5] -z-0" />
                        <div
                            className="absolute top-4 left-4 h-[2px] bg-ink -z-0 transition-all duration-700"
                            style={{
                                width: currentIdx <= 0
                                    ? "0%"
                                    : `calc(${(currentIdx / (STEPS.length - 1)) * 100}% - ${currentIdx * (32 / (STEPS.length - 1))}px)`,
                            }}
                        />
                        {STEPS.map((step, idx) => {
                            const isDone = idx < currentIdx;
                            const isCurrent = idx === currentIdx;
                            const ts = stageAt[step];
                            return (
                                <div
                                    key={step}
                                    data-testid={`tracker-step-${step.toLowerCase()}`}
                                    data-state={isDone ? "done" : isCurrent ? "current" : "upcoming"}
                                    className="relative z-10 flex flex-col items-center gap-1.5 flex-1 min-w-0"
                                >
                                    <div
                                        className={`w-8 h-8 flex items-center justify-center transition-colors ${
                                            isDone
                                                ? "bg-ink text-white"
                                                : isCurrent
                                                    ? "bg-white border-2 border-ink text-ink"
                                                    : "bg-white border-2 border-[#E5E5E5] text-[#A3A3A3]"
                                        }`}
                                    >
                                        {isDone ? (
                                            <CheckCircle size={14} weight="fill" />
                                        ) : isCurrent ? (
                                            <Clock size={14} weight="bold" />
                                        ) : (
                                            <span className="text-[10px] font-black">{idx + 1}</span>
                                        )}
                                    </div>
                                    <p
                                        className={`text-[10px] font-black uppercase tracking-[0.15em] text-center leading-tight ${
                                            isDone || isCurrent ? "text-ink" : "text-[#A3A3A3]"
                                        }`}
                                    >
                                        {step}
                                    </p>
                                    {ts && (
                                        <p className="text-[9px] text-[#A3A3A3] tabular-nums">
                                            {fmtDate(ts)}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Upcoming interview banner */}
            {!isRejected && upcoming && (
                <div
                    data-testid={`app-tracker-${application.id}-upcoming-interview`}
                    className="mt-5 border-l-4 border-l-cobalt bg-cobalt/5 px-4 py-3 flex items-start gap-3"
                >
                    <CalendarBlank size={18} weight="bold" className="text-cobalt shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-black uppercase tracking-wider text-cobalt mb-0.5">
                            Interview scheduled
                            {upcoming.round ? <> · Round {upcoming.round}</> : null}
                        </p>
                        <p className="text-sm font-bold text-ink">{fmtDateTime(upcoming.scheduled_at)}</p>
                        <p className="text-xs text-[#525252] mt-0.5">
                            {upcoming.mode || "Interview"}
                            {upcoming.duration_min ? <> · {upcoming.duration_min} min</> : null}
                            {upcoming.location ? <> · {upcoming.location}</> : null}
                        </p>
                        {upcoming.meeting_url && (
                            <a
                                href={upcoming.meeting_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`app-tracker-${application.id}-join-link`}
                                className="inline-block mt-2 text-xs font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark underline underline-offset-2"
                            >
                                Join meeting &rarr;
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Offer summary */}
            {stage === "Offer" && application.offer_outcome && (
                <div className="mt-5 border-l-4 border-l-emerald-500 bg-emerald-50 px-4 py-3">
                    <p className="text-xs font-black uppercase tracking-wider text-emerald-900 mb-1">
                        Offer {application.offer_outcome.status || "extended"}
                    </p>
                    {application.expected_joining_date && (
                        <p className="text-xs text-emerald-900">
                            Expected joining: <span className="font-bold">{fmtDate(application.expected_joining_date)}</span>
                            {application.offered_ctc_amount ? (
                                <> · CTC {application.offered_ctc_amount} {application.offered_ctc_currency || "INR"}</>
                            ) : null}
                        </p>
                    )}
                </div>
            )}

            {/* Application boost — impulse-purchase visibility bump */}
            {!isRejected && stage !== "Hired" && (
                <div
                    data-testid={`app-tracker-${application.id}-boost`}
                    className="mt-5 border-t border-[#F5F5F5] pt-4 flex items-center justify-between gap-3 flex-wrap"
                >
                    {boostActive ? (
                        <>
                            <div className="flex items-center gap-2 text-xs">
                                <span
                                    data-testid={`app-tracker-${application.id}-boost-active`}
                                    className="inline-flex items-center gap-1.5 bg-splash text-ink px-2.5 py-1 font-black uppercase tracking-wider"
                                >
                                    <Lightning size={11} weight="fill" /> Boost active
                                </span>
                                <span className="text-[#525252]">
                                    Top of recruiter shortlist until {fmtDate(boost.active_until)}
                                </span>
                            </div>
                            <button
                                onClick={startBoost}
                                disabled={buying}
                                data-testid={`app-tracker-${application.id}-boost-extend`}
                                className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark disabled:opacity-60"
                            >
                                {buying ? "Opening…" : "Extend +7 days →"}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold text-ink">
                                    <Lightning size={11} weight="fill" className="inline mb-0.5 text-splash" /> Boost this application
                                </p>
                                <p className="text-[11px] text-[#525252]">
                                    ₹49 · Top of the recruiter's list for this role for 7 days
                                </p>
                            </div>
                            <button
                                onClick={startBoost}
                                disabled={buying}
                                data-testid={`app-tracker-${application.id}-boost-cta`}
                                className="bg-ink hover:bg-cobalt text-white px-3.5 py-2 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                            >
                                <Lightning size={11} weight="fill" /> {buying ? "Opening…" : "Boost ₹49"}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
